/**
 * "Example clips" picker — search CMS clips (clips API v2) and multi-select them
 * to attach to a theme.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Checkbox, CircularProgress, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";

import TSPrimaryButton from "../../../../components/common/buttons/TSPrimaryButton";
import TSSecondaryButton from "../../../../components/common/buttons/TSSecondaryButton";

import { searchClips } from "./clipsApi";
import type { CmsClip } from "./types";

interface ClipPickerModalProps {
  /** Clip ids already attached/selected, shown as checked + disabled. */
  attachedIds?: Set<string>;
  onClose: () => void;
  onDone: (clips: CmsClip[]) => void;
  /** Rendered in place of ThemeModal's own content (no modal-on-modal) — the header shows
   * a "← Example clips" back row that calls this to return instead of a standalone Dialog. */
  onBack: () => void;
}

function formatDuration(seconds?: number): string {
  if (!seconds || seconds < 0) return "";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function ClipPickerModal({ attachedIds, onClose, onDone, onBack }: ClipPickerModalProps) {
  const [search, setSearch] = useState("");
  const [clips, setClips] = useState<CmsClip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<string, CmsClip>>({});

  // Debounced search.
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    setLoading(true);
    setError(null);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await searchClips({ search, pageSize: 40 });
        setClips(res.items);
      } catch (e: any) {
        setError(e?.message || "Could not load clips");
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const toggle = (clip: CmsClip) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[clip._id]) delete next[clip._id];
      else next[clip._id] = clip;
      return next;
    });
  };

  const selectedCount = Object.keys(selected).length;
  const done = () => {
    onDone(Object.values(selected));
    onClose();
  };

  const rows = useMemo(() => clips, [clips]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "85vh", maxHeight: "85vh" }}>
      <Box sx={{ p: "24px 24px 0", flexShrink: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb="16px">
          <Box display="flex" alignItems="center" gap="8px">
            <IconButton
              onClick={onBack}
              size="small"
              aria-label="Back"
              sx={{ ml: "-8px", "&:focus": { outline: "none" } }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#212427" }}>Example clips</Typography>
          </Box>
          <Typography sx={{ fontSize: "13px", color: "#606971" }}>
            {selectedCount > 0 ? `${selectedCount} selected` : ""}
          </Typography>
        </Box>

        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search clips…"
          fullWidth
          size="small"
          autoFocus
          sx={{ mb: "16px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", minHeight: 0, p: "0 24px" }}>
        <Box sx={{ mx: "-4px" }}>
          {loading && (
            <Box display="flex" justifyContent="center" py="32px">
              <CircularProgress size={24} sx={{ color: "#1A7E70" }} />
            </Box>
          )}
          {!loading && error && (
            <Typography sx={{ color: "#B91C1C", fontSize: "13px", py: "24px", textAlign: "center" }}>
              {error}
            </Typography>
          )}
          {!loading && !error && rows.length === 0 && (
            <Typography sx={{ color: "#606971", fontSize: "14px", py: "32px", textAlign: "center" }}>
              {search ? "No clips match your search." : "No clips found."}
            </Typography>
          )}
          {!loading &&
            !error &&
            rows.map((clip) => {
              const alreadyAttached = attachedIds?.has(clip._id) || false;
              const checked = alreadyAttached || Boolean(selected[clip._id]);
              return (
                <Box
                  key={clip._id}
                  onClick={() => !alreadyAttached && toggle(clip)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    p: "8px",
                    borderRadius: "8px",
                    cursor: alreadyAttached ? "default" : "pointer",
                    opacity: alreadyAttached ? 0.55 : 1,
                    "&:hover": { backgroundColor: alreadyAttached ? "transparent" : "#F7F9FC" },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 40,
                      borderRadius: "6px",
                      background: "#F1F2F3",
                      flexShrink: 0,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {clip.thumbnailURL ? (
                      <Box
                        component="img"
                        src={clip.thumbnailURL}
                        alt=""
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <VideocamOutlinedIcon sx={{ color: "#9AA1A8", fontSize: 20 }} />
                    )}
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#212427",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {clip.title || "Untitled clip"}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#606971" }}>{formatDuration(clip.duration)}</Typography>
                  </Box>
                  <Checkbox checked={checked} disabled={alreadyAttached} sx={{ "&:focus": { outline: "none" } }} />
                </Box>
              );
            })}
        </Box>
      </Box>

      <Box sx={{ flexShrink: 0, p: "16px 24px", borderTop: "1px solid #E5E7EB" }}>
        <Box display="flex" justifyContent="flex-end" gap="12px">
          <TSSecondaryButton text="Cancel" onClick={onClose} />
          <TSPrimaryButton
            text={`Add ${selectedCount || ""} clip${selectedCount === 1 ? "" : "s"}`.trim()}
            onClick={done}
            disabled={selectedCount === 0}
            sx={{ fontSize: "15px" }}
          />
        </Box>
      </Box>
    </Box>
  );
}

export { formatDuration };
